import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DriversManagementApiService } from '../../../services/drivers-management-api.service';
import { Driver } from '../../../models/driver';
import { DriverAssignment } from '../../../models/driver-assignment';
import { DriverDelivery } from '../../../models/driver-delivery';
import { GenericPaginationResponseBody } from '../../../../../shared/models/Generic-pagination-response-body';
import { LimitOffsetPagination } from '../../../../../shared/models/limit-offset-pagination';
import { ToastNotificationService } from '../../../../../notifications/services/toast-notification.service';
import { ToastNotificationType, ToastPosition } from '../../../../../notifications/models/toast-notification-type';
import { TablePaginationComponent } from '../../../../../shared/components/table-pagination/table-pagination.component';
import { LanguageService } from '../../../../../shared/services/translations/language.service';

type DriverTab = 'history' | 'deliveries';

@Component({
  selector: 'app-driver-details-page',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslateModule, TablePaginationComponent],
  templateUrl: './driver-details-page.component.html',
})
export class DriverDetailsPageComponent implements OnInit {

  private route = inject(ActivatedRoute);
  private apiService = inject(DriversManagementApiService);
  private toast = inject(ToastNotificationService);
  private translate = inject(TranslateService);
  languageService = inject(LanguageService);

  driverId: string | null = null;
  driver: Driver | null = null;
  loading = false;
  togglingActive = false;

  activeTab: DriverTab = 'history';

  assignments: GenericPaginationResponseBody<DriverAssignment> | undefined;
  assignmentsPagination: LimitOffsetPagination = { limit: 5, offset: 0 };
  assignmentsLoaded = false;

  deliveries: GenericPaginationResponseBody<DriverDelivery> | undefined;
  deliveriesPagination: LimitOffsetPagination = { limit: 5, offset: 0 };
  deliveriesLoaded = false;

  ngOnInit(): void {
    this.driverId = this.route.snapshot.paramMap.get('id');
    if (!this.driverId) return;

    this.loading = true;
    this.apiService.getDriverById(this.driverId).subscribe({
      next: (response) => {
        this.driver = response;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toast.createNotification(this.translate.instant('drivers_management.details.errors.load_message'), ToastNotificationType.ERROR, this.translate.instant('drivers_management.details.errors.load_title'), ToastPosition.TOP_CENTER);
      }
    });

    this.loadAssignments();
  }

  toggleActive(): void {
    if (!this.driver || this.togglingActive) return;
    const next = !this.driver.is_active;
    this.togglingActive = true;
    this.driver.is_active = next;
    this.apiService.setDriverActive(this.driver.id, next).subscribe({
      next: () => {
        this.togglingActive = false;
        this.toast.createNotification(this.translate.instant('drivers_management.details.toggle_active.success_message'), ToastNotificationType.SUCCESS, this.translate.instant('drivers_management.details.toggle_active.success_title'), ToastPosition.BOTTOM_RIGHT);
      },
      error: () => {
        if (this.driver) this.driver.is_active = !next;
        this.togglingActive = false;
        this.toast.createNotification(this.translate.instant('drivers_management.details.toggle_active.error_message'), ToastNotificationType.ERROR, this.translate.instant('drivers_management.details.toggle_active.error_title'), ToastPosition.TOP_CENTER);
      }
    });
  }

  switchTab(tab: DriverTab): void {
    this.activeTab = tab;
    if (tab === 'history' && !this.assignmentsLoaded) this.loadAssignments();
    if (tab === 'deliveries' && !this.deliveriesLoaded) this.loadDeliveries();
  }

  loadAssignments(): void {
    if (!this.driverId) return;
    this.apiService.getDriverAssignmentHistory(this.driverId, this.assignmentsPagination).subscribe({
      next: (response) => { this.assignments = response; this.assignmentsLoaded = true; },
      error: () => { this.assignmentsLoaded = true; }
    });
  }

  loadDeliveries(): void {
    if (!this.driverId) return;
    this.apiService.getDriverDeliveries(this.driverId, this.deliveriesPagination).subscribe({
      next: (response) => { this.deliveries = response; this.deliveriesLoaded = true; },
      error: () => { this.deliveriesLoaded = true; }
    });
  }

  updateAssignmentsPagination(pagination: LimitOffsetPagination): void {
    this.assignmentsPagination = pagination;
    this.loadAssignments();
  }

  updateDeliveriesPagination(pagination: LimitOffsetPagination): void {
    this.deliveriesPagination = pagination;
    this.loadDeliveries();
  }
}
